import { useEffect, useRef, useState } from "react";
import { cn } from "@/utils/classes";

/* Prop Types */
export interface VideoScenePillarTabOption<TKey extends string> {
  key: TKey;
  label: string;
  sublabel?: string;
  color: string;
}

interface VideoScenePillarTabProps<TKey extends string> {
  label: string;
  color: string;
  options: VideoScenePillarTabOption<TKey>[];
  selectedKeys: Set<TKey>;
  onToggle: (key: TKey) => void;
}

/**
 * VideoScenePillarTab component
 *
 * A pillar tab (Attention / Emotion / Memory) for the scene performance graph. Opens a dropdown of
 * checkboxes for the pillar's quality scores, and shows a count badge once any are selected.
 */
export const VideoScenePillarTab = <TKey extends string>({ label, color, options, selectedKeys, onToggle }: VideoScenePillarTabProps<TKey>) => {
  /* State */
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /* Consts */
  const selectedCount = options.filter((option) => selectedKeys.has(option.key)).length;
  const isActive = selectedCount > 0;

  // Closes the dropdown on any click outside the tab or its menu
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  /* Render */
  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={cn(
          "flex items-center gap-1.5 px-3 py-[7px] rounded-md border-[1.5px] text-[13px] leading-[18px] font-semibold cursor-pointer",
          isActive ? "text-white" : "bg-[#323c49] text-[#9ca3af]",
          isOpen ? "border-white" : "border-transparent",
        )}
        style={isActive ? { backgroundColor: `${color}B3` } : undefined}
      >
        {label}
        {isActive && <span className="px-1.5 py-px rounded-lg bg-white/25 text-[11px] leading-[15px]">{selectedCount}</span>}
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-2 z-20 flex flex-col gap-0.5 w-[250px] p-2.5 rounded-[10px] border border-[#3a4350] bg-[#1d232a] shadow-[0px_8px_24px_0px_rgba(0,0,0,0.35)]"
        >
          {options.map((option) => {
            const isChecked = selectedKeys.has(option.key);
            return (
              <label key={option.key} className="flex items-center gap-3 p-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => onToggle(option.key)}
                  className="checkbox checkbox-xs rounded-sm border-[1.5px] border-[#6b7380]"
                  style={isChecked ? ({ "--input-color": option.color, borderColor: option.color, backgroundColor: option.color } as React.CSSProperties) : undefined}
                />
                <span className="flex-1 text-[13px] leading-[18px] font-semibold text-[#e9e9ec]">{option.label}</span>
                {option.sublabel && <span className="text-[11px] leading-[15px] font-medium text-[#6b7380]">{option.sublabel}</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};
